/** Last applied API theme tokens — applied before Angular boot to avoid default sage flash. */
export const ADMIN_THEME_CACHE_KEY = "saferoads-admin-theme-tokens-v1";
export const ADMIN_THEME_LOGO_CACHE_KEY = "saferoads-admin-theme-logo-v1";

export function readCachedAdminThemeLogo(): string | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  try {
    return localStorage.getItem(ADMIN_THEME_LOGO_CACHE_KEY) || null;
  } catch {
    return null;
  }
}

export function writeCachedAdminThemeLogo(url: string): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(ADMIN_THEME_LOGO_CACHE_KEY, url);
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Update every <link rel="icon"> and <link rel="apple-touch-icon"> in the
 * document to point at the supplied URL so the browser tab favicon always
 * reflects the currently uploaded logo rather than the build-time static file.
 */
export function updateFavicon(url: string): void {
  if (typeof document === "undefined" || !url?.trim()) {
    return;
  }
  const selectors = [
    'link[rel="icon"]',
    'link[rel="shortcut icon"]',
    'link[rel="apple-touch-icon"]',
  ];
  selectors.forEach((sel) => {
    document.querySelectorAll<HTMLLinkElement>(sel).forEach((el) => {
      el.href = url;
    });
  });
}

export function readCachedAdminThemeTokens(): Record<string, string> | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  try {
    const raw = localStorage.getItem(ADMIN_THEME_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, string>;
  } catch {
    return null;
  }
}

export function writeCachedAdminThemeTokens(tokens: Record<string, string>): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(ADMIN_THEME_CACHE_KEY, JSON.stringify(tokens));
  } catch {
    // ignore quota / private mode
  }
}

/** Apply backend token map to document CSS variables (safe to call before Angular bootstrap). */
export function applyAdminThemeTokens(tokens: Record<string, string>): void {
  if (typeof document === "undefined") {
    return;
  }
  const root = document.documentElement;
  for (const [name, value] of Object.entries(tokens)) {
    if (!name || value == null || value === "") {
      continue;
    }
    root.style.setProperty(`--saferoads-${name}`, value);
  }
  syncPrimaryRgb(tokens);
  syncListToolbarFromPrimary(tokens);
  syncHeaderChromeFromPrimary(tokens);
  syncBrandLinkedTokens(tokens);
  syncBrowserThemeColor(tokens);
}

function syncPrimaryRgb(tokens: Record<string, string>): void {
  const primary = tokens["primary"]?.trim();
  if (!primary) {
    return;
  }
  const rgb = hexToRgbTriplet(primary);
  if (rgb) {
    document.documentElement.style.setProperty("--saferoads-primary-rgb", rgb);
  }
}

function syncListToolbarFromPrimary(tokens: Record<string, string>): void {
  const primary = tokens["primary"]?.trim();
  if (!primary) {
    return;
  }
  const wash = mixHexTowardWhite(primary, 0.91);
  if (!wash) {
    return;
  }
  const root = document.documentElement;
  root.style.setProperty("--saferoads-list-toolbar-bg", wash);
  root.style.setProperty("--saferoads-list-toolbar-bg-end", wash);
  root.style.setProperty("--saferoads-list-toolbar-from-primary", wash);
  root.style.setProperty("--saferoads-list-toolbar-gradient", wash);
}

function syncHeaderChromeFromPrimary(tokens: Record<string, string>): void {
  const primary = tokens["primary"]?.trim();
  if (!primary) {
    return;
  }
  const root = document.documentElement;
  const header = tokens["header-background"]?.trim() || primary;
  root.style.setProperty("--saferoads-header-background", header);
  root.style.setProperty("--saferoads-header-gradient", header);
  root.style.setProperty("--saferoads-toolbar-gradient", header);
}

function syncBrandLinkedTokens(tokens: Record<string, string>): void {
  const root = document.documentElement;
  for (const name of [
    "green-mint",
    "green-pale",
    "green-pale-border",
    "green-pale-hover",
    "green-chip-bg",
    "green-chip-text",
    "green-chip-border",
    "green-highlight",
    "green-highlight-text",
    "green-bright",
    "green-subtle-bg",
    "green-subtle-border",
    "green-subtle-text",
    "green-subtle-text-muted",
    "fee-green-dark",
    "fee-green-bg",
    "audit-green",
    "audit-indigo-bg",
    "audit-indigo-border",
    "audit-indigo-text",
    "audit-indigo-bg-alt",
    "audit-purple-bg",
    "audit-purple-border",
    "audit-purple-text",
    "audit-violet-text",
    "audit-surface",
    "audit-surface-alt",
    "blue-indigo",
    "indigo-14",
    "indigo-35",
    "match-green",
    "match-green-border",
    "match-green-bg",
    "match-green-bg-alt",
    "match-green-text",
    "match-green-dark",
    "match-border",
    "match-bg",
  ]) {
    root.style.removeProperty(`--saferoads-${name}`);
  }

  const primary = tokens["primary"]?.trim();
  if (!primary) {
    return;
  }
  const primaryDark = tokens["primary-dark"]?.trim() || primary;
  const rgb = hexToRgbTriplet(primary);
  if (!rgb) {
    return;
  }
  root.style.setProperty("--saferoads-green-panel-bg", `rgba(${rgb}, 0.14)`);
  root.style.setProperty("--saferoads-enrollment-green", primary);
  root.style.setProperty("--saferoads-enrollment-green-bg", `rgba(${rgb}, 0.06)`);
  root.style.setProperty("--saferoads-enrollment-green-accent", primary);
  root.style.setProperty("--saferoads-enrollment-green-gradient", primary);
  root.style.setProperty("--saferoads-class-green-dark", primaryDark);
  root.style.setProperty("--saferoads-class-green-text", primary);
  root.style.setProperty("--saferoads-class-green-deep", primaryDark);
  root.style.setProperty("--saferoads-class-green-accent", primary);
  root.style.setProperty("--saferoads-class-green-muted", primary);
  root.style.setProperty("--saferoads-class-green-darker", primaryDark);
}

function syncBrowserThemeColor(tokens: Record<string, string>): void {
  const isPlainColor = (v?: string) => !!v && /^(#|rgb|hsl)/i.test(v.trim());
  const header = tokens["header-background"];
  const color = (isPlainColor(header) ? header : tokens["primary"])?.trim();
  if (!isPlainColor(color) || typeof document === "undefined") {
    return;
  }
  // Safari ignores in-place content changes on theme-color; replace the tag so the tab bar repaints.
  document.querySelectorAll('meta[name="theme-color"]').forEach((el) => el.remove());
  const meta = document.createElement("meta");
  meta.name = "theme-color";
  meta.content = color;
  document.head.appendChild(meta);
}

function mixHexTowardWhite(hex: string, whiteAmount: number): string | null {
  let normalized = hex.replace("#", "").trim();
  if (normalized.length === 3) {
    normalized = normalized
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (normalized.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }
  const keep = 1 - whiteAmount;
  const mix = (i: number) =>
    Math.round(parseInt(normalized.slice(i, i + 2), 16) * keep + 255 * whiteAmount);
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(mix(0))}${toHex(mix(2))}${toHex(mix(4))}`;
}

function hexToRgbTriplet(hex: string): string | null {
  let normalized = hex.replace("#", "").trim();
  if (normalized.length === 3) {
    normalized = normalized
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (normalized.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}
